import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, Outlet, useNavigate, useParams } from '@tanstack/react-router';
import { useDocumentParams } from 'app/router/params';
import ReferenceDataDetails from './details';
import { ReferenceDetailsProvider, useReferenceDetails } from './ReferenceDetailsContext';
import moment from 'moment';
import { LazyTippy, Loader, Dropdown } from 'app/components';
import { v4 as uuidv4 } from 'uuid';
import { FileZone } from 'app/components';
import ReferenceDataDropdownMenu from './reference-data-dropdown-menu';
import LocaleBadge from 'app/components/layout/LocaleBadge';
import {
  useCreateReferenceData,
  useReferenceList,
  useUpdateReferenceData,
  useGetXliffDownload,
  useReferenceTranslations,
  useUploadXliff,
  usePreviewXliff,
} from 'entities/reference-data';
import XliffPreviewModal, { type XliffPreview } from './XliffPreviewModal';
import LanguageSelector from 'shared/forms/LanguageSelector';
import { useGetDocument } from 'entities/document';
import type { EditorDocument, ReferenceDataValue } from 'entities/document';
import { useUser } from 'app/state/contexts/UserContext';
import Form from 'react-bootstrap/Form';
import { fuzzySearch } from 'entities/reference-data';

// Batch-loaded reference-list record: the shared ReferenceDataValue shape.
export type ReferenceListEntry = ReferenceDataValue;

interface XliffDownloadRow {
  data: BlobPart;
  targetLocale: string;
  sourceLocale: string;
}

export default function ReferenceData() {
  const { t } = useTranslation(['organization-menu', 'reference-data']);

  const { documentId } = useDocumentParams();
  const navigate = useNavigate();
  const { refId } = useParams({ strict: false });
  const { selectedOrganization } = useUser();
  const { data: pDocument } = useGetDocument(documentId, selectedOrganization);
  const { data: referenceList = [], isLoading, isFetching } = useReferenceList(pDocument, { keepPreviousList: true });
  const { data: translations, isLoading: isLoadingTranslations } = useReferenceTranslations(pDocument?.superId);
  const { mutate: uploadXliff, isPending: isImporting } = useUploadXliff(pDocument?.superId);
  const { mutate: previewXliff, isPending: isPreviewing } = usePreviewXliff(pDocument?.superId);
  const { mutate: updateReferenceData } = useUpdateReferenceData();
  const { mutate: getXliffDownload } = useGetXliffDownload(pDocument?.superId);
  const [showDetails, setShowDetails] = useState(false);
  const [showNewReferenceForm, setShowNewReferenceForm] = useState(false);
  const [showNewXliffForm, setShowNewXliffForm] = useState(false);
  const [existingReference, setExistingReference] = useState<ReferenceListEntry | null>(null);
  const [referenceSearch, setReferenceSearch] = useState('');
  const [refreshTranslation, setRefreshTranslation] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<XliffPreview | null>(null);
  const [pendingXliffId, setPendingXliffId] = useState<string | null>(null);
  const [viewLocale, setViewLocale] = useState<string | undefined>(pDocument?.content?.locale);
  useEffect(() => {
    if (isLoading || isFetching) {
      return;
    }

    if (referenceList.length > 0 && !refId) {
      navigate({ to: '/documents/$documentId/reference-data/$refId', params: { documentId, refId: referenceList[0].superId ?? '' }, replace: true });
    } else if (refId && !referenceList.find((r: ReferenceListEntry) => r.superId === refId)) {
      navigate({ to: '/documents/$documentId/reference-data', params: { documentId }, replace: true });
    }
  }, [referenceList, refId, navigate, documentId, isLoading, isFetching]);
  const xliff = () => {
    getXliffDownload(undefined, {
      onSuccess: (results) => {
        results.data.forEach(({ data, targetLocale, sourceLocale }: XliffDownloadRow) => {
          const url = window.URL.createObjectURL(new Blob([data], { type: 'text/xml' }));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `translation_${moment(new Date()).format('YYYY-MM-DD HH:mm')}_${sourceLocale}_to_${targetLocale}.xliff`);

          document.body.appendChild(link);
          link.click();
          link.remove();
        });
      },
    });
  };

  const onChangeName = (name: ReferenceListEntry) => {
    setExistingReference(name);
    setShowNewReferenceForm(true);
  };

  const handleReferenceCreated = useCallback(
    (referenceSuperId: string) => {
      setShowNewReferenceForm(false);
      navigate({ to: '/documents/$documentId/reference-data/$refId', params: { documentId, refId: referenceSuperId } });
    },
    [documentId, navigate]
  );

  if (!pDocument) {
    return <Loader />;
  }

  const handleNewRefClick = () => {
    setShowNewReferenceForm(true);
    setExistingReference(null);
  };

  const handleNewXliffClick = () => {
    setShowNewXliffForm(!showNewXliffForm);
  };

  const triggerPreview = (xliffId: string) => {
    setPendingXliffId(xliffId);
    previewXliff(xliffId, {
      onSuccess: (data) => setPreviewData((data ?? null) as XliffPreview | null),
    });
  };

  const onUploadSuccess = ({ data }: { data: { revisionId: string } }) => {
    setShowNewXliffForm(false);
    triggerPreview(data.revisionId);
  };

  const onConfirmImport = ({ skipSourceChanged }: { skipSourceChanged: boolean }) => {
    if (!pendingXliffId) return;
    uploadXliff(
      { xliffId: pendingXliffId, skipSourceChanged },
      {
        onSuccess: () => {
          setPreviewData(null);
          setPendingXliffId(null);
        },
      }
    );
  };

  const onClosePreview = () => {
    setPreviewData(null);
    setPendingXliffId(null);
  };

  const onLocaleChange = (item: ReferenceListEntry, locale: string) => {
    const updatedItem = { ...item, revisionId: uuidv4(), locale };
    updateReferenceData({ document: pDocument, referenceData: updatedItem });
  };

  if (isLoading) {
    return <Loader />;
  }
  return (
    <>
      <XliffPreviewModal show={!!previewData} onClose={onClosePreview} preview={previewData} onConfirm={onConfirmImport} isImporting={isImporting} />
      <div className="overview--reference--menu">
        <button className="button default bold" onClick={xliff} type="button" disabled={referenceList?.length === 0}>
          {t('reference-data:download-xliff')}
        </button>
        <LazyTippy
          interactive={true}
          visible={showNewXliffForm}
          onClickOutside={() => setShowNewXliffForm(false)}
          offset={[20, 10]}
          placement="bottom"
          content={<FileZone type="xliff" documentId={documentId} onSuccess={onUploadSuccess} />}
          disabled={referenceList.length === 0}
        >
          <button className="button default bold" onClick={() => handleNewXliffClick()} type="button">
            {t('reference-data:upload-xliff')}
          </button>
        </LazyTippy>
        <button className="button default bold" onClick={() => setShowDetails(!showDetails)} type="button" disabled={referenceList.length === 0}>
          {t('reference-data:upload-file')}
        </button>
        <LazyTippy
          interactive={true}
          visible={showNewReferenceForm}
          onClickOutside={() => setShowNewReferenceForm(false)}
          offset={[20, 10]}
          placement="bottom"
          content={<NewReferenceForm pDocument={pDocument} onReferenceCreated={handleReferenceCreated} existingReference={existingReference} />}
        >
          <button className="fab" type="button" onClick={() => handleNewRefClick()}>
            <i className="fa-regular fa-plus font-15" />
          </button>
        </LazyTippy>
        {(translations?.length ?? 0) > 0 && <LanguageSelector locale={viewLocale} onChange={setViewLocale} sm />}
        <Dropdown
          items={translations?.map((translation: { filename: string; revisionId: string }) => ({
            title: translation.filename,
            value: translation.revisionId,
          }))}
          value={refreshTranslation}
          disabled={isLoadingTranslations}
          onChange={(value) => setRefreshTranslation(value as string)}
        />
        <button
          className="fab"
          disabled={!refreshTranslation || isPreviewing}
          onClick={() => {
            if (refreshTranslation) triggerPreview(refreshTranslation);
          }}
        >
          {isPreviewing ? <span className="spinner-border spinner-border-sm" /> : <i className="fa-light fa-arrows-rotate"></i>}
        </button>
      </div>
      <div className="grid--container reference-data--layout">
        <div className="reference-data--list">
          <Form.Control
            autoFocus
            className="my-2 w-100 py-2"
            placeholder={t('organization-menu:filter')}
            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setReferenceSearch(e.target.value)}
            value={referenceSearch}
            size="sm"
          />
          {fuzzySearch(referenceList, referenceSearch).map((item: ReferenceListEntry, index: number) => (
            <ReferenceListItem
              item={item}
              onChangeName={onChangeName}
              setShowDetails={setShowDetails}
              key={index}
              onLocaleChange={onLocaleChange}
              documentLocale={pDocument?.content?.locale}
            />
          ))}
        </div>
        <div className="card info-card">
          <ReferenceDetailsProvider value={{ showDetails, setShowDetails, viewLocale }}>
            <Outlet />
          </ReferenceDetailsProvider>
        </div>
      </div>
    </>
  );
}

interface ReferenceListItemProps {
  item: ReferenceListEntry;
  onChangeName: (item: ReferenceListEntry) => void;
  setShowDetails: (show: boolean) => void;
  onLocaleChange: (item: ReferenceListEntry, locale: string) => void;
  documentLocale?: string;
}

function ReferenceListItem({ item, onChangeName, setShowDetails, onLocaleChange, documentLocale }: ReferenceListItemProps) {
  const { t } = useTranslation('reference-data');

  const { documentId } = useDocumentParams();
  const { refId } = useParams({ strict: false });
  const active = refId === item.superId;
  return (
    <Link to="/documents/$documentId/reference-data/$refId" params={{ documentId, refId: item.superId ?? '' }} key={item.superId}>
      <div key={item.id} className={`d-flex flex-row align-items-center py-4 ${active ? 'bg-white' : ''}`}>
        <i className={`fa-regular fa-grip-lines drag-drop text-blue-500 px-2 ${active ? '' : 'invisible'}`} style={{ fontSize: '1.25rem' }}></i>
        <div className="flex-grow-1 overflow-hidden pe-3">
          <h4 className="two-line-overflow">{item.name ? item.name : t('item.missing-file')}</h4>
          <div className="d-flex align-items-center gap-2">
            <p className="date-text m-0">{moment(item.createdAt).format('D MMM. YYYY, HH:mm')}</p>
            <LocaleBadge locale={item.locale} fallbackLocale={documentLocale} />
          </div>
        </div>
        {active && (
          <ReferenceDataDropdownMenu
            documentId={documentId}
            item={item}
            onChangeName={onChangeName}
            setShowDetails={setShowDetails}
            onLocaleChange={onLocaleChange}
          />
        )}
      </div>
    </Link>
  );
}

interface NewReferenceFormProps {
  pDocument: EditorDocument;
  onReferenceCreated: (referenceSuperId: string) => void;
  existingReference: ReferenceListEntry | null;
}

function NewReferenceForm({ pDocument, onReferenceCreated, existingReference }: NewReferenceFormProps) {
  const { t } = useTranslation('reference-data');

  const [newReferenceName, setNewReferenceName] = useState(existingReference?.name || '');
  const { mutate: createReferenceData } = useCreateReferenceData();
  const { mutate: updateReferenceData } = useUpdateReferenceData();
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!newReferenceName) {
      return;
    }

    const referenceData = {
      superId: uuidv4(),
      documentSuperId: pDocument.superId,
      ...(existingReference || {}),
      name: newReferenceName,
      revisionId: uuidv4(),
    };
    const persistReference = (existingReference ? updateReferenceData : createReferenceData) as (
      variables: { document: EditorDocument; referenceData: ReferenceDataValue },
      options?: { onSuccess?: () => void }
    ) => void;
    persistReference(
      { document: pDocument, referenceData },
      {
        onSuccess: () => {
          setNewReferenceName('');
          onReferenceCreated?.(referenceData.superId ?? '');
        },
      }
    );
  };

  return (
    <div className="position-absolute bg-white border border-line rounded px-3 py-r-1" style={{ right: 0, zIndex: 1 }}>
      <form onSubmit={onSubmit}>
        <div className="d-flex flex-row gap-r-1 border border-line rounded border-blue-800 px-3 py-r-1">
          <input
            className="all-unset text-nowrap"
            style={{ width: '250px' }}
            type="text"
            value={newReferenceName}
            ref={inputRef}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setNewReferenceName(event.target.value)}
          />
          <button type="submit" className="all-unset d-block text-nowrap fw-bold fs-5 text-uppercase">
            {existingReference ? t('dropdown.edit-name') : t('item.dropdown.create-name')}
          </button>
        </div>
      </form>
    </div>
  );
}

export function ReferenceList() {
  const { showDetails, setShowDetails, viewLocale } = useReferenceDetails();
  return <ReferenceDataDetails showDetails={showDetails} setShowDetails={setShowDetails} viewLocale={viewLocale} />;
}
